import { useCallback, useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/services/firebase/init";
import { workspaceRequest } from "@/services/firebase/artistWorkspace";
import { Button } from "@/components/ui/button";

interface Props {
  artistId: string;
  contentId: string;
  assetId: string;
  version?: number;
  revisionId?: string;
  ready: boolean;
}

/** The HTTPS copy is private; every renewal rechecks the exact draft/revision. */
export function ArtistVlogPreview({ artistId, contentId, assetId, version, revisionId, ready }: Props) {
  const video = useRef<HTMLVideoElement>(null);
  const generation = useRef(0);
  const position = useRef(0);
  const resume = useRef(false);
  const expiry = useRef(0);
  const pending = useRef(false);
  const [url, setUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async (forceResume = false) => {
    if (!ready || pending.current) return;
    pending.current = true;
    const request = ++generation.current;
    position.current = video.current?.currentTime || 0;
    resume.current = forceResume || (video.current !== null && !video.current.paused);
    setBusy(true); setError(undefined);
    try {
      const grant = await workspaceRequest<{ url: string; expiresAt: number }>("videoPreviewUrl", artistId, {
        contentId, assetId, workspace: !revisionId,
        ...(revisionId ? { expectedRevisionId: revisionId } : { expectedVersion: version }),
      });
      if (request !== generation.current) return;
      const parsed = new URL(grant.url);
      if (parsed.protocol !== "https:" || parsed.hostname !== "storage.googleapis.com" || grant.expiresAt <= Date.now()) throw new Error("無效的預覽授權");
      expiry.current = grant.expiresAt;
      setUrl(grant.url);
    } catch (e) {
      if (request === generation.current) {setUrl(undefined); setError(e instanceof Error ? e.message : "預覽未完成，請重試。");}
    } finally {if (request === generation.current) setBusy(false); pending.current = false;}
  }, [artistId, contentId, assetId, version, revisionId, ready]);
  useEffect(() => {
    pending.current = false; expiry.current = 0; setUrl(undefined); position.current = 0; resume.current = false;
    void refresh();
    const timer = window.setInterval(() => {if (video.current && !video.current.paused) void refresh();}, 120000);
    const invalidate = () => {generation.current++;};
    const uid = auth.currentUser?.uid;
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (uid !== user?.uid) {generation.current++; video.current?.pause(); setUrl(undefined); setError("登入已變更，請重新開啟內容。");}
    });
    return () => {invalidate(); window.clearInterval(timer); unsubscribe();};
  }, [refresh]);
  if (!ready) return <p className="text-sm text-muted-foreground">影片尚未完成播放處理，請稍後重新載入。</p>;
  return <section aria-label="Vlog 雲端審核預覽" className="space-y-2">
    {url && <video ref={video} src={url} controls playsInline preload="metadata" crossOrigin="anonymous"
      className="max-h-[520px] w-full bg-black" onLoadedMetadata={() => {
        if (!video.current) return;
        video.current.currentTime = Math.min(position.current, Math.max(0, video.current.duration - 1));
        if (resume.current) void video.current.play().catch(() => undefined);
      }} onPlay={() => {if (expiry.current <= Date.now() + 15000) {video.current?.pause(); resume.current = true; void refresh(true);}}}
      onError={() => setError("影片連線已中斷或授權已到期，請更新預覽。")} />}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    <Button variant="outline" disabled={busy} onClick={() => void refresh()}>{busy ? "取得預覽中…" : "更新影片預覽"}</Button>
    <p className="text-xs text-muted-foreground">HTTPS 私有預覽；授權到期後需重新確認目前版本與權限。</p>
  </section>;
}
